/*
  Remind me before a date (snippets/mm-remind.liquid).

  Opens and closes the inline panel, checks the fields, and posts the reminder to the
  mm-wishlist-sync service (/api/remind) as text/plain JSON, which skips the CORS preflight.

  The service replies with a manage token. It is kept in this browser (localStorage key
  mmRemindToken) so the next reminder joins the same list and /pages/reminders can show it without
  a link. The token is sent back only to the service, never placed in a URL by this file.
  Nothing else about the shopper is stored in the browser.

  Words: the snippet renders the locale keys under mm.remind into
  <script type="application/json" data-mm-remind-i18n>. Each key falls back to the English
  in TEXT below when it is missing (Shopify prints "translation missing" until the keys exist).
*/
(function () {
  if (customElements.get('mm-remind')) return;

  var TOKEN_KEY = 'mmRemindToken';
  var DAYS_IN = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

  var TEXT = {
    save: 'Save the date',
    saving: 'Saving',
    err_nickname_missing: 'Add a nickname for the person, such as Mum.',
    err_nickname_long: 'Keep the nickname to 30 characters.',
    err_nickname_contact: 'Use only a nickname here, not an email or phone number.',
    err_date_missing: 'Choose the day and the month.',
    err_date_invalid: 'That month does not have that many days.',
    err_contact: 'Enter a valid email or a 10 digit mobile number.',
    err_consent: 'Tick the consent box so we can save this.',
    err_busy: 'Too many tries. Wait a few minutes and try again.',
    err_limit: 'You have reached the limit of 12 reminders. Delete one to add another.',
    err_rejected: 'Something in the form was not accepted. Check the nickname, the date and your contact.',
    err_server: 'We could not save that just now. Try again shortly.',
    err_offline: 'We could not save that just now. Check your connection and try again.',
    done_email_yearly: 'We will write to you by email 10 days before, with a few ideas, and again each year until you delete it.',
    done_email_once: 'We will write to you by email 10 days before, with a few ideas.',
    done_whatsapp_yearly: 'We will write to you on WhatsApp 10 days before, with a few ideas, and again each year until you delete it.',
    done_whatsapp_once: 'We will write to you on WhatsApp 10 days before, with a few ideas.'
  };

  /* Locale text from the page, English fallback for anything missing or empty. The t filter
     HTML-escapes, so each phrase is decoded once before it is used as textContent. */
  function readText(scope) {
    var out = {};
    var given = {};
    var decoder = document.createElement('textarea');
    try { given = JSON.parse(scope.querySelector('[data-mm-remind-i18n]').textContent) || {}; } catch (e) { given = {}; }
    Object.keys(TEXT).forEach(function (key) {
      var value = given[key];
      if (typeof value === 'string' && value.trim() && !/translation missing/i.test(value)) {
        decoder.innerHTML = value;
        out[key] = decoder.value;
      } else {
        out[key] = TEXT[key];
      }
    });
    return out;
  }

  function readToken() { try { return localStorage.getItem(TOKEN_KEY) || ''; } catch (e) { return ''; } }
  function saveToken(t) { try { localStorage.setItem(TOKEN_KEY, t); } catch (e) { /* private mode */ } }

  function isEmail(v) { return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v); }
  function mobileDigits(v) {
    if (!/^[\d\s+()-]+$/.test(v)) return '';
    var d = v.replace(/\D/g, '');
    if (d.length < 10 || d.length > 13) return '';
    d = d.slice(-10);
    return /^[6-9]\d{9}$/.test(d) ? d : '';
  }
  function looksLikeContact(v) { return v.indexOf('@') !== -1 || v.replace(/\D/g, '').length >= 7; }

  function track(name, data) {
    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') Shopify.analytics.publish(name, data);
    } catch (e) { /* ignore */ }
  }

  class MmRemind extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.toggle = this.querySelector('[data-mm-remind-toggle]');
      this.panel = this.querySelector('[data-mm-remind-panel]');
      this.form = this.querySelector('[data-mm-remind-form]');
      this.done = this.querySelector('[data-mm-remind-done]');
      this.msg = this.querySelector('[data-mm-remind-msg]');
      this.submit = this.querySelector('[data-mm-remind-submit]');
      this.text = readText(this);
      if (!this.toggle || !this.panel || !this.form) return;

      this.toggle.addEventListener('click', this.onToggle.bind(this));
      this.form.addEventListener('submit', this.onSubmit.bind(this));
      this.form.addEventListener('input', this.clearMessage.bind(this));
      var again = this.querySelector('[data-mm-remind-again]');
      if (again) again.addEventListener('click', this.reset.bind(this));
      this.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && !this.panel.hidden && this.toggle.getAttribute('aria-expanded') === 'true') { this.setOpen(false); this.toggle.focus(); }
      }.bind(this));
    }

    setOpen(open) {
      this.panel.hidden = !open;
      this.toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    onToggle() {
      var open = this.panel.hidden;
      this.setOpen(open);
      if (open) {
        var first = this.form.hidden ? this.done : this.form.querySelector('select, input');
        if (first) first.focus({ preventScroll: true });
        track('reminder_open', {});
      }
    }

    clearMessage() { if (this.msg) { this.msg.textContent = ''; this.msg.classList.remove('is-visible'); } }
    showError(text, field) {
      if (this.msg) { this.msg.textContent = text; this.msg.classList.add('is-visible'); }
      if (field) field.focus();
    }

    setBusy(on) {
      this.busy = on;
      if (!this.submit) return;
      this.submit.disabled = on;
      this.submit.textContent = on ? this.text.saving : this.text.save;
    }

    onSubmit(e) {
      e.preventDefault();
      if (this.busy) return;
      var f = this.form.elements;
      var nickname = (f.nickname.value || '').replace(/\s+/g, ' ').trim();
      var day = parseInt(f.day.value, 10);
      var month = parseInt(f.month.value, 10);
      var contactRaw = (f.contact.value || '').trim();
      var consent = this.form.querySelector('.dpdp-consent__checkbox');

      var t = this.text;
      if (!nickname) return this.showError(t.err_nickname_missing, f.nickname);
      if (nickname.length > 30) return this.showError(t.err_nickname_long, f.nickname);
      if (looksLikeContact(nickname)) return this.showError(t.err_nickname_contact, f.nickname);
      if (!day || !month) return this.showError(t.err_date_missing, !day ? f.day : f.month);
      if (day > DAYS_IN[month - 1]) return this.showError(t.err_date_invalid, f.day);
      var email = isEmail(contactRaw);
      var mobile = email ? '' : mobileDigits(contactRaw);
      if (!email && !mobile) return this.showError(t.err_contact, f.contact);
      if (!consent || !consent.checked) return this.showError(t.err_consent, consent);

      var payload = {
        occasion: f.occasion.value,
        nickname: nickname,
        date: { day: day, month: month },
        recurring: !!f.recurring.checked,
        contact: email ? contactRaw : mobile,
        consent: true
      };
      if (f.mood.value) payload.mood = f.mood.value;
      if (f.budget.value) payload.budget = f.budget.value;
      var token = readToken();
      if (token) payload.token = token;

      var self = this;
      this.clearMessage();
      this.setBusy(true);
      fetch(this.dataset.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify(payload)
      }).then(function (r) {
        return r.json().catch(function () { return {}; }).then(function (j) { return { status: r.status, body: j }; });
      }).then(function (res) {
        self.setBusy(false);
        if (res.status === 200 && res.body.ok) {
          if (res.body.token) saveToken(res.body.token);
          track('reminder_saved', { occasion: payload.occasion, recurring: payload.recurring, channel: email ? 'email' : 'whatsapp' });
          return self.showDone(payload, email);
        }
        if (res.status === 429) return self.showError(t.err_busy);
        if (res.status === 409) return self.showError(t.err_limit);
        if (res.status === 400) return self.showError(t.err_rejected);
        self.showError(t.err_server);
      }).catch(function () {
        self.setBusy(false);
        self.showError(t.err_offline);
      });
    }

    showDone(payload, email) {
      var text = this.querySelector('[data-mm-remind-done-text]');
      if (text) {
        /* One whole sentence per case, so editors never see a sentence built from pieces. */
        var key = 'done_' + (email ? 'email' : 'whatsapp') + '_' + (payload.recurring ? 'yearly' : 'once');
        text.textContent = this.text[key];
      }
      this.form.hidden = true;
      if (this.done) { this.done.hidden = false; this.done.focus({ preventScroll: true }); }
      this.dispatchEvent(new CustomEvent('mm-remind:saved', { bubbles: true }));
    }

    reset() {
      var f = this.form.elements;
      f.nickname.value = '';
      f.day.value = '';
      f.month.value = '';
      var consent = this.form.querySelector('.dpdp-consent__checkbox');
      if (consent) consent.checked = false;
      if (this.done) this.done.hidden = true;
      this.form.hidden = false;
      f.occasion.focus({ preventScroll: true });
    }
  }

  customElements.define('mm-remind', MmRemind);
})();
